<script setup lang="ts">
import type { PreviewItemData } from '@autobid/strapi-integration/typescript/auction/carPreview'
import type { Category } from '@autobid/strapi-integration/typescript/auction/categories'
import type { AuctionCar } from '@autobid/ui/types/Car'
import { getError } from '@autobid/strapi-integration/utils/getError'
import Spinner from '@autobid/ui/components/common/Spinner.vue'
import { useCategories } from '@autobid/ui/composables/car/useCategories'
import { useProfile } from '@autobid/ui/composables/useProfile'
import { parseGefcoTransportPayload } from '@autobid/ui/utils/car/parseTransportPayload'
import { useQuery } from '@tanstack/vue-query'

interface Props {
  car: AuctionCar | PreviewItemData
}

const props = defineProps<Props>()

const isOpen = ref(false)
const { locale, t } = useI18n()
const { data: profile } = useProfile()
const push = usePush()

const ADSpUrl = computed(() => {
  if (locale.value === 'de') {
    return 'https://cms.autobid.de/uploads/DSLV_AD_Sp_2017_DE_7f008bf27d.pdf'
  }
  return 'https://cms.autobid.de/uploads/DSLV_AD_Sp_2017_EN_0bbd342d31.pdf'
})

const { categories, isLoading: areCategoriesLoading } = useCategories({
  enabled: computed(() => isOpen.value && locale.value !== 'en'),
  lang: 'en',
})

function getCategoryEngName(categories: Category[], id: number) {
  const match = categories.find(category => category.id === id)
  if (!match) {
    throw new Error('Category not found')
  }
  return match.name
}
const { $trpc } = useNuxtApp()

const { data: calculation, isLoading: isCalculationLoading } = useQuery({
  queryKey: ['transport-gefco-calculation', locale, profile.value?.id, props.car.id],
  queryFn: async () => {
    if (!profile.value || !('category' in props.car))
      return null

    const categoryNameEng
      = locale.value === 'en'
        ? props.car.category.name
        : getCategoryEngName(categories.value || [], props.car.category.id)

    if (locale.value !== 'en' && !categories.value) {
      throw new Error('Categories not loaded')
    }

    const payload = parseGefcoTransportPayload({
      categoryNameEng,
      car: props.car,
      profile: profile.value,
    })

    const result = await $trpc.towTruck.getGefcoCalculation.mutate(payload)

    if (!result) {
      throw new Error('No result')
    }

    if (!result.data.success) {
      throw new Error(
        // The API sometimes sends the message as empty string, therefore we display the error from our i18n in this case
        result.data.error.message?.length ? result.data.error.message : t(`transport-modal.error.400`),
      )
    }

    // info: current implementation returns only one offer, so we should be headed to the first one
    return { payload, result: result.data.offers[0] }
  },
  onError: (error: unknown) => {
    if (!isOpen.value)
      return

    const { message, code, handled } = getError(error)

    if (!handled) {
      push.error([422, 400].includes(code) ? t(`transport-modal.error.${code}`) : message)
    }

    isOpen.value = false
  },
  enabled: computed(() =>
    Boolean(isOpen.value && (categories.value || locale.value === 'en')),
  ),
  cacheTime: 1000 * 60 * 5,
})

const shouldDialogBeShown = computed(() => {
  return Boolean(isOpen.value && calculation.value)
})
</script>

<template>
  <CommonTooltip :tooltip="$t('cars-list.transport-tooltip')">
    <DynamicAuctionItemListCarIcon
      variant="border"
      size="big"
      v-bind="$attrs"
      @click.stop="isOpen = true"
    >
      <Spinner
        v-if="
          (isCalculationLoading || (areCategoriesLoading && locale !== 'en'))
            && isOpen
        "
      />
      <i v-else class="ab-icon ab-icon-transport h-full" />
    </DynamicAuctionItemListCarIcon>
  </CommonTooltip>

  <CommonDialog :opened="shouldDialogBeShown" @close="isOpen = false">
    <template #title>
      {{ $t('transport-modal.title') }}
    </template>
    <template v-if="calculation" #content>
      <div class="text-lg">
        <i18n-t keypath="transport-modal.calculation-info" tag="div">
          <template #distance>
            <div class="my-2 border-l-2 border-l-primary pl-4">
              <p>
                {{ `${$t('post-code')} D-${calculation.payload.origin.zip}` }}
              </p>
            </div>
          </template>
          <template #destination>
            <div class="my-2 border-l-2 border-l-primary pl-4">
              <p>
                {{
                  profile?.extendedData?.addressBook?.items[
                    profile.extendedData.addressBook.vehicleDeliveryAddress
                  ].companyName
                }}
              </p>
              <p>{{ calculation.payload.destination.street }}</p>
              <p>
                {{
                  `${calculation.payload.destination.zip} ${calculation.payload.destination.city}`
                }}
              </p>
            </div>
          </template>
          <template #amount>
            <span class="font-bold">{{ calculation.result.base_price }} €</span>
          </template>
        </i18n-t>
        <p class="mt-4">
          {{ $t('transport-modal.additional-info') }}
        </p>
        <i18n-t
          class="mt-4"
          keypath="transport-modal.additional-info-2"
          tag="p"
        >
          <template #link>
            <CommonLink underline class="text-primary" :href="ADSpUrl">
              (ADSp)
            </CommonLink>
          </template>
        </i18n-t>
        <p class="mt-4 italic">
          {{ $t('transport-modal.footer') }}
        </p>
      </div>
    </template>
  </CommonDialog>
</template>
